import { useEffect, useState } from 'react';
import { ApiError, agentTitle, type AgentRow, type Api } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';

type Card = AgentRow & { persona?: string; brand?: string; draftDiffers: boolean };

/** Agents — stats, the build→publish→test path and one card per agent (layout from the legacy panel). */
export const AgentsPage = ({ api, t, onNew, onOpen }: { api: Api; t: T; onNew: () => void; onOpen: (id: string) => void }) => {
  // ADR-071: a new agent is made in the studio, which needs centeros.agents; the cards stay open to everyone who may see.
  const canCreate = useCan()(PERM.agents);
  const [rows, setRows] = useState<Card[]>();
  const [error, setError] = useState<string>();
  const [filter, setFilter] = useState<'all' | 'outbound' | 'inbound' | 'live' | 'draft'>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    api
      .agents()
      .then(async (r) => {
        setRows(r.items.map((a) => ({ ...a, draftDiffers: false })));
        // Details for the cards (persona, unpublished changes) — a handful of agents per business.
        const details = await Promise.allSettled(r.items.slice(0, 24).map((a) => api.agent(a.id)));
        setRows(
          r.items.map((a, i) => {
            const d = details[i];
            if (!d || d.status !== 'fulfilled') return { ...a, draftDiffers: false };
            const spec = d.value.spec?.spec as { identity?: { persona?: string; brandName?: string } } | undefined;
            return {
              ...a,
              ...(spec?.identity?.persona ? { persona: spec.identity.persona } : {}),
              ...(spec?.identity?.brandName ? { brand: spec.identity.brandName } : {}),
              draftDiffers: Boolean(d.value.published?.draftDiffers),
            };
          }),
        );
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api]);

  const all = rows ?? [];
  const live = all.filter((a) => a.publishedPackageId);
  const q = query.trim().toLowerCase();
  const shown = all
    .filter((a) => filter === 'all' || (filter === 'live' ? a.publishedPackageId : filter === 'draft' ? !a.publishedPackageId || a.draftDiffers : a.directionDefault === filter || a.directionDefault === 'both'))
    .filter((a) => !q || a.name.toLowerCase().includes(q) || (a.label ?? '').toLowerCase().includes(q) || (a.brand ?? '').toLowerCase().includes(q));
  const status = (a: Card) => (!a.publishedPackageId ? 'draft' : a.draftDiffers ? 'changed' : 'live');

  return (
    <section>
      <div className="card hero">
        <div className="row-between">
          <div className="hero-title">
            <span className="hero-icon" aria-hidden>✦</span>
            <div>
              <h1>{t('agents.title')}</h1>
              <p className="muted">{t('agents.subtitle', { n: all.length })}</p>
            </div>
          </div>
          {canCreate && <button type="button" className="primary" onClick={onNew}>✦ {t('agents.new')}</button>}
        </div>
        <div className="path">
          {(['create', 'tune', 'publish', 'test'] as const).map((s, i) => (
            <span key={s} className="path-step"><span className="step-no">{i + 1}</span>{t(`agents.path.${s}`)}</span>
          ))}
        </div>
        <div className="kpis four">
          <div className="kpi static"><span className="kpi-label">{t('agents.stat.total')}</span><span className="kpi-value">{all.length}</span></div>
          <div className="kpi static"><span className="kpi-label">{t('agents.stat.outbound')}</span><span className="kpi-value">{all.filter((a) => a.directionDefault !== 'inbound').length}</span></div>
          <div className="kpi static"><span className="kpi-label">{t('agents.stat.inbound')}</span><span className="kpi-value">{all.filter((a) => a.directionDefault !== 'outbound').length}</span></div>
          <div className="kpi static ok"><span className="kpi-label">{t('agents.stat.live')}</span><span className="kpi-value">{live.length}</span></div>
        </div>
      </div>

      {error && <div className="banner error">{t(`error.${error}`)}</div>}
      {rows && rows.length === 0 && (
        <div className="empty">
          <p>{t('agents.empty')}</p>
          {canCreate && <button type="button" className="primary" onClick={onNew}>{t('agents.new')}</button>}
        </div>
      )}
      {rows && rows.length > 0 && (
        <>
          <div className="toolbar section-gap">
            {(['all', 'outbound', 'inbound', 'live', 'draft'] as const).map((f) => (
              <button key={f} type="button" className={`chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{t(`agents.filter.${f}`)}</button>
            ))}
            <input className="search push" type="search" value={query} placeholder={t('agents.search')} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div className="agent-cards">
            {shown.map((a) => (
              <button key={a.id} type="button" className="agent-card" onClick={() => onOpen(a.id)}>
                <span className="row-between">
                  <span className="agent-avatar" aria-hidden>{agentTitle(a).slice(0, 1).toUpperCase()}</span>
                  <span className={`status ${status(a)}`}>{t(`agent.status.${status(a)}`)}</span>
                </span>
                <strong className="agent-name">{agentTitle(a)}</strong>
                {a.label && <span className="hint">{t('agent.label.speaking', { name: a.name })}</span>}
                <span className="hint">{[a.brand, t(`direction.${a.directionDefault}`)].filter(Boolean).join(' · ')}</span>
                {a.persona && <span className="agent-persona">{a.persona}</span>}
                <span className="agent-open">{t('agents.open')} →</span>
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
};
